import { keepPreviousData } from '@tanstack/react-query';

/**
 * Stale-while-revalidate cache configuration for TanStack Query.
 *
 * Optimal for slow-changing data that benefits from instant loading:
 * - Dashboard metrics (hourly/daily aggregates)
 * - Settings and configuration data
 * - Infrequently updated tables (vulnerabilities, assets)
 * - Statistical summaries and reports
 *
 * Strategy:
 * - Shows cached data immediately (0ms perceived latency)
 * - Revalidates in background (transparent updates)
 * - 10-minute freshness window (safe for metrics/settings)
 *
 * Performance Impact:
 * - First load: 300-700ms (API fetch with loading state)
 * - Subsequent loads: 0ms (instant from cache)
 * - Background refresh: Transparent to user
 * - API load reduction: ~90% for repeat visits
 *
 * @example Dashboard widgets
 * const { data, status, isPlaceholderData } = useMy(
 *   { resource: 'statistic', query: '...' },
 *   STALE_WHILE_REVALIDATE_CACHE
 * );
 *
 * @example Vulnerability table
 * const { data } = useQuery(
 *   { queryKey: ['vulnerabilities'], queryFn: fetchVulns },
 *   STALE_WHILE_REVALIDATE_CACHE
 * );
 *
 * @example Settings section
 * const { data } = useQuery(
 *   { queryKey: ['integrations'], queryFn: fetchIntegrations },
 *   STALE_WHILE_REVALIDATE_CACHE
 * );
 */
export const STALE_WHILE_REVALIDATE_CACHE = {
  staleTime: 10 * 60 * 1000, // 10 minutes - data considered fresh
  gcTime: 30 * 60 * 1000, // 30 minutes - cache retention
  refetchOnMount: false, // Don't refetch if data is fresh
  refetchOnWindowFocus: false, // Don't refetch on window focus
  refetchOnReconnect: false, // Don't refetch on network reconnect
  placeholderData: keepPreviousData, // Show old data while fetching new
  retry: 1,
  retryDelay: 500,
} as const;
